import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { History, Filter, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Tag } from 'lucide-react';
import { toast } from 'sonner';

const ADJUSTMENT_LABELS = {
  percent_increase: 'Aumento %',
  percent_decrease: 'Rebaja %',
  fixed_increase: 'Aumento $',
  fixed_decrease: 'Rebaja $',
  set_price: 'Precio fijo',
};

export default function PricingHistory() {
  const { user } = useAuth();
  const isMaster = ['master', 'superadmin'].includes(user?.role_id);
  const [actions, setActions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filters, setFilters] = useState({ company_id: '', from_date: '', to_date: '' });

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = async () => {
    try {
      const compsRes = await api.getCompanies();
      setCompanies(compsRes.data);
      await load();
    } catch (e) {
      toast.error('SalesPRO: Error al cargar histórico');
    } finally {
      setLoading(false);
    }
  };

  const load = async () => {
    const params = {};
    if (filters.company_id) params.company_id = filters.company_id;
    if (filters.from_date) params.from_date = filters.from_date;
    if (filters.to_date) params.to_date = filters.to_date;
    const res = await api.getPricingActions(params);
    setActions(res.data);
  };

  const applyFilters = async () => {
    try { await load(); } catch (e) { toast.error('SalesPRO: Error al filtrar'); }
  };

  const companyName = (id) => companies.find(c => c.company_id === id)?.name || id;

  const isIncrease = (t) => t === 'percent_increase' || t === 'fixed_increase';

  if (loading) return <div className="flex items-center justify-center h-full">Cargando SalesPRO...</div>;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="pricing-history-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22] flex items-center gap-2">
          <History size={26} /> Histórico de Acciones de Precio
        </h1>
        <p className="text-sm text-[#64748B] mt-1">Registro inmutable de cambios masivos de precios</p>
      </div>

      {/* Filtros */}
      <Card className="border border-[#E2E8F0]">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            {isMaster && (
              <div className="flex-1">
                <Label className="text-xs">Empresa</Label>
                <select
                  value={filters.company_id}
                  onChange={(e) => setFilters({ ...filters, company_id: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
                  data-testid="ph-company-filter"
                >
                  <option value="">Todas</option>
                  {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div className="flex-1">
              <Label className="text-xs">Desde</Label>
              <Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} data-testid="ph-from-date" />
            </div>
            <div className="flex-1">
              <Label className="text-xs">Hasta</Label>
              <Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} data-testid="ph-to-date" />
            </div>
            <Button onClick={applyFilters} data-testid="ph-apply-filters">
              <Filter size={16} className="mr-1.5" /> Filtrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {actions.length === 0 ? (
        <div className="text-center py-12 text-[#64748B]" data-testid="ph-empty">No hay acciones registradas</div>
      ) : (
        <div className="space-y-3">
          {actions.map((a) => (
            <Card key={a.audit_id} className="border border-[#E2E8F0]" data-testid={`ph-action-${a.audit_id}`}>
              <CardContent className="p-3 sm:p-4">
                <button
                  className="w-full flex items-center justify-between gap-2 text-left"
                  onClick={() => setExpanded(expanded === a.audit_id ? null : a.audit_id)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2 rounded-md ${isIncrease(a.adjustment_type) ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                      {isIncrease(a.adjustment_type) ? <TrendingUp size={18} /> : (a.adjustment_type === 'set_price' ? <Tag size={18} /> : <TrendingDown size={18} />)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm text-[#022C22] truncate">
                        {ADJUSTMENT_LABELS[a.adjustment_type] || a.adjustment_type}: {a.value}{a.adjustment_type.startsWith('percent') ? '%' : ' $'}
                        {a.label ? ` — ${a.label}` : ''}
                      </p>
                      <p className="text-xs text-[#64748B] truncate">
                        {companyName(a.company_id)} · {a.products_count} producto(s) · {(a.created_at || '').slice(0, 16).replace('T', ' ')}
                      </p>
                      {a.reason && <p className="text-xs text-[#94A3B8] italic truncate">Motivo: {a.reason}</p>}
                    </div>
                  </div>
                  {expanded === a.audit_id ? <ChevronUp size={18} className="text-[#94A3B8] shrink-0" /> : <ChevronDown size={18} className="text-[#94A3B8] shrink-0" />}
                </button>

                {expanded === a.audit_id && (
                  <div className="mt-3 pt-3 border-t border-[#E2E8F0] overflow-x-auto">
                    <table className="w-full text-sm min-w-[400px]">
                      <thead>
                        <tr className="text-left text-[#64748B] border-b border-[#E2E8F0]">
                          <th className="py-2 pr-3">Producto</th>
                          <th className="py-2 pr-3 text-right">Antes</th>
                          <th className="py-2 text-right">Después</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(a.changes || []).map((c, i) => (
                          <tr key={i} className="border-b border-[#F1F5F9] last:border-0">
                            <td className="py-1.5 pr-3">{c.name}</td>
                            <td className="py-1.5 pr-3 text-right text-[#64748B]">${Number(c.old_price).toFixed(2)}</td>
                            <td className="py-1.5 text-right font-medium">${Number(c.new_price).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
